import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, useMotionValueEvent, useScroll, useTransform } from 'framer-motion';

import useAnimate from '../ui/Motion';

import Reveal from '../ui/Reveal';

/* ─────────────────────────────────────────────────────────────────
   Scroll-scrubbed image sequence: a pinned canvas whose frame index
   is driven by the reader's position through the section.

   This replaces a version that set `img.src` on every scroll event.
   Two problems with that: swapping the src of a live <img> hands the
   browser a decode on the main thread at paint time, which is what
   made the sequence stutter, and there was no rAF gate, so a trackpad
   flick queued a state update per wheel tick. Here every frame is
   decoded once, up front, into an offscreen bitmap, and drawing one
   is a single drawImage into a canvas that is already the right size.

   The pin is earned: the whole point is that scrolling advances the
   footage, so every pixel of the runway is spent showing the reader
   something they have not seen. That is the one case where holding
   the viewport still is not a tax.
   ───────────────────────────────────────────────────────────────── */

/* Above this the last frames are still arriving while the reader is
   already scrubbing, and the sequence visibly snaps to whatever has
   landed. Decoding in order and tracking how far the contiguous run
   reaches lets the scrub clamp to what is actually ready. */
const PRIORITY = 12;

export default function FrameScrubber({
    path,
    count,
    runway = 300,
    focus = 0.5,
    eyebrow,
    title,
    body,
    captions = []
}) {
    const sectionRef = useRef(null);
    const canvasRef = useRef(null);
    const framesRef = useRef([]);
    const readyRef = useRef(0);
    const drawnRef = useRef(-1);
    const rafRef = useRef(0);
    const reduced = !useAnimate();

    const [ready, setReady] = useState(0);

    const src = i => `${path}frame_${String(i + 1).padStart(4, '0')}.jpg`;

    /* Cover math, done against the canvas's backing-store size so it is
       correct on a HiDPI display. `focus` is the horizontal point of the
       frame to hold when the crop is tighter than the source: the subject
       of these sequences is rarely dead centre. */
    const paint = useCallback(index => {
        const canvas = canvasRef.current;
        const image = framesRef.current[index];

        if (!canvas || !image || !image.complete || !image.naturalWidth) return;

        const ctx = canvas.getContext('2d', { alpha: false });
        const { width, height } = canvas;

        const scale = Math.max(width / image.naturalWidth, height / image.naturalHeight);
        const w = image.naturalWidth * scale;
        const h = image.naturalHeight * scale;

        ctx.drawImage(image, (width - w) * focus, (height - h) / 2, w, h);
        drawnRef.current = index;
    }, [focus]);

    /* Decode in order so `readyRef` can be a single contiguous high-water
       mark rather than a set of holes, and prioritise the opening frames
       so the section is never a black rectangle when it scrolls in. */
    useEffect(() => {
        let cancelled = false;
        framesRef.current = new Array(count);

        const load = i =>
            new Promise(resolve => {
                const image = new Image();
                image.decoding = 'async';
                image.onload = image.onerror = () => resolve();
                image.src = src(i);
                framesRef.current[i] = image;
            });

        (async () => {
            for (let i = 0; i < Math.min(PRIORITY, count); i += 1) {
                await load(i);
                if (cancelled) return;
                readyRef.current = i + 1;
            }

            if (!cancelled) {
                setReady(readyRef.current);
                paint(0);
            }

            for (let i = PRIORITY; i < count; i += 1) {
                await load(i);
                if (cancelled) return;
                readyRef.current = i + 1;
            }

            if (!cancelled) setReady(readyRef.current);
        })();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [path, count]);

    /* The backing store is sized in device pixels and the element in CSS
       pixels, so a 2x display gets a 2x bitmap instead of an upscaled one.
       Resizing a canvas clears it, hence the repaint of the last frame. */
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const resize = () => {
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            const rect = canvas.getBoundingClientRect();

            canvas.width = Math.round(rect.width * dpr);
            canvas.height = Math.round(rect.height * dpr);

            paint(Math.max(0, drawnRef.current));
        };

        resize();
        window.addEventListener('resize', resize);

        return () => window.removeEventListener('resize', resize);
    }, [paint]);

    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ['start start', 'end end']
    });

    /* One paint per animation frame, whatever the input device is doing.
       A trackpad can emit wheel events faster than the display refreshes,
       and without this gate each one would queue its own drawImage. */
    useMotionValueEvent(scrollYProgress, 'change', value => {
        if (reduced) return;

        const limit = Math.max(1, readyRef.current);
        const wanted = Math.min(limit - 1, Math.floor(value * count));

        if (wanted === drawnRef.current) return;

        cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(() => paint(wanted));
    });

    useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

    const veilOpacity = useTransform(scrollYProgress, [0, 0.12, 0.9, 1], [0.55, 0.3, 0.3, 0.7]);

    return (
        <section
            className="scrub"
            ref={sectionRef}
            style={{ '--runway': `${reduced ? 100 : runway}vh` }}
        >
            <div className="scrub__pin">
                <canvas className="scrub__canvas" ref={canvasRef} aria-hidden="true" />
                <motion.div
                    className="scrub__veil"
                    style={reduced ? undefined : { opacity: veilOpacity }}
                    aria-hidden="true"
                />

                <div className="shell scrub__copy">
                    {eyebrow && (
                        <Reveal y={16}>
                            <span className="t-eyebrow t-eyebrow--accent">{eyebrow}</span>
                        </Reveal>
                    )}

                    <Reveal delay={0.08} y={24}>
                        <h2 className="t-h2 scrub__title">{title}</h2>
                    </Reveal>

                    {body && (
                        <Reveal delay={0.16} y={20}>
                            <p className="t-lead scrub__body">{body}</p>
                        </Reveal>
                    )}

                    {captions.length > 0 && (
                        <ul className="scrub__captions">
                            {captions.map(caption => (
                                <Caption key={caption.text} caption={caption} progress={scrollYProgress}
                                    reduced={reduced} />
                            ))}
                        </ul>
                    )}
                </div>

                {/* Honest loading state: the bar only reports frames that have
                    actually decoded, and it removes itself once the sequence
                    can be scrubbed end to end. */}
                {ready < count && (
                    <div className="scrub__progress" aria-hidden="true">
                        <span style={{ transform: `scaleX(${ready / count})` }} />
                    </div>
                )}
            </div>
        </section>
    );
}

/* Hooks cannot run inside .map(), so each caption owns its own window. */
function Caption({ caption, progress, reduced }) {
    const [a, b] = caption.at;
    const opacity = useTransform(progress, [a - 0.08, a, b, b + 0.08], [0, 1, 1, 0]);
    const y = useTransform(progress, [a - 0.08, a], ['12px', '0px']);

    return (
        <motion.li style={reduced ? undefined : { opacity, y }}>
            <span className="scrub__caption-dot" />
            {caption.text}
        </motion.li>
    );
}
